import styled, { css } from 'styled-components';
import { MMediaStackImageDirection, MMediaStackAspectRatio, MMediaStackImageLayout } from './MMediaStack.types';
import { baseVariables } from '@marriott/mi-ui-library';

interface WrapperProps {
  $imageDirection: MMediaStackImageDirection;
  $aspectRatio: MMediaStackAspectRatio;
  $imageLayout: MMediaStackImageLayout;
}

interface MediaProps {
  $imageDirection: MMediaStackImageDirection;
  $imageLayout: MMediaStackImageLayout;
  $aspectRatio: MMediaStackAspectRatio;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Return the correct mobile aspect-ratio for the wrapper based on Figma measurements:
//   Diagonal 2:3 → 343×540  |  Diagonal 1:1 → 343×432  |  Centered → 343×523
// Tablet/desktop: fixed regardless of aspectRatio prop (OEditorialHighlight overrides with height:100%)
const isCenteredStack = (dir: MMediaStackImageDirection, ar: MMediaStackAspectRatio) =>
  dir === MMediaStackImageDirection.Centered || ar === MMediaStackAspectRatio.OneOnePlusTwoThree;

const wrapperMobileRatio = (dir: MMediaStackImageDirection, ar: MMediaStackAspectRatio): string => {
  if (isCenteredStack(dir, ar)) return '343 / 523';
  return ar === MMediaStackAspectRatio.OneOne ? '343 / 432' : '343 / 540';
};

const primaryCss = (dir: MMediaStackImageDirection, layout: MMediaStackImageLayout, ar: MMediaStackAspectRatio) => {
  const isCentered = isCenteredStack(dir, ar);
  const isReverse = layout === MMediaStackImageLayout.Reverse;
  const is1x1 = ar === MMediaStackAspectRatio.OneOne;
  const AR = is1x1 ? '1 / 1' : '2 / 3';

  if (isCentered) {
    // Centered: primary always fills full width at 1:1.
    // Default → sits near top;  Reverse → sits lower (secondary is above it)
    // Figma mobile 343×523: Default top=64/523=12.24%, Reverse top=116/523=22.18%
    // Figma tablet 286×512: Default top=103/512=20.12%, Reverse top=123/512=24.02%
    // Figma desktop 558×810: Default top=84/810=10.37%, Reverse top=169/810=20.86%
    return isReverse
      ? css`
          left: 0;
          width: 100%;
          aspect-ratio: 1 / 1;
          top: 22.18%;
          @media ${baseVariables.mediaQuery.md} {
            top: 24.02%;
          }
          @media ${baseVariables.mediaQuery.lg} {
            top: 20.86%;
          }
        `
      : css`
          left: 0;
          width: 100%;
          aspect-ratio: 1 / 1;
          top: 12.24%;
          @media ${baseVariables.mediaQuery.md} {
            top: 20.12%;
          }
          @media ${baseVariables.mediaQuery.lg} {
            top: 10.37%;
          }
        `;
  }

  // Diagonal
  // Default → top-left;  Reverse → bottom-right (mirrors Default)
  // Offsets (% of wrapper height) by breakpoint + aspectRatio:
  //   Mobile  2:3 → 11.85%  |  1:1 → 14.81%
  //   Tablet  2:3 →  9.77%  |  1:1 → 20.12%
  //   Desktop 2:3 →  8.27%  |  1:1 → 19.26%
  // Width (% of wrapper width) by breakpoint: Mobile=65%, Tablet=78.32%, Desktop=65.23%
  const mobileOffset = is1x1 ? '14.81%' : '11.85%';
  const tabletOffset = is1x1 ? '20.12%' : '9.77%';
  const desktopOffset = is1x1 ? '19.26%' : '8.27%';

  return isReverse
    ? css`
        right: 0;
        width: 65%;
        aspect-ratio: ${AR};
        bottom: ${mobileOffset};
        @media ${baseVariables.mediaQuery.md} {
          width: 78.32%;
          bottom: ${tabletOffset};
        }
        @media ${baseVariables.mediaQuery.lg} {
          width: 65.23%;
          bottom: ${desktopOffset};
        }
      `
    : css`
        left: 0;
        width: 65%;
        aspect-ratio: ${AR};
        top: ${mobileOffset};
        @media ${baseVariables.mediaQuery.md} {
          width: 78.32%;
          top: ${tabletOffset};
        }
        @media ${baseVariables.mediaQuery.lg} {
          width: 65.23%;
          top: ${desktopOffset};
        }
      `;
};

const secondaryCss = (dir: MMediaStackImageDirection, layout: MMediaStackImageLayout, ar: MMediaStackAspectRatio) => {
  const isCentered = isCenteredStack(dir, ar);
  const isReverse = layout === MMediaStackImageLayout.Reverse;
  const is1x1 = ar === MMediaStackAspectRatio.OneOne;
  const AR = is1x1 ? '1 / 1' : '2 / 3';

  if (isCentered) {
    // Centered: secondary always 2:3, horizontally inset from both sides.
    // Default → sits lower (below primary);  Reverse → sits higher (above primary)
    // Figma mobile 343×523: Default top=124/523=23.71%, Reverse top=64/523=12.24%
    // Figma tablet 286×512: Default top=165/512=32.23%, Reverse top=104/512=20.31%
    // Figma desktop 558×810: Default top=180/810=22.22%, Reverse top=84/810=10.37%
    // Left offset: mobile/desktop=17.38–17.49%, tablet=21.68%
    return isReverse
      ? css`
          left: 17.49%;
          width: 65%;
          aspect-ratio: 2 / 3;
          top: 12.24%;
          @media ${baseVariables.mediaQuery.md} {
            left: 21.68%;
            width: 56.64%;
            top: 20.31%;
          }
          @media ${baseVariables.mediaQuery.lg} {
            left: 17.38%;
            width: 65.23%;
            top: 10.37%;
          }
        `
      : css`
          left: 17.49%;
          width: 65%;
          aspect-ratio: 2 / 3;
          top: 23.71%;
          @media ${baseVariables.mediaQuery.md} {
            left: 21.68%;
            width: 56.64%;
            top: 32.23%;
          }
          @media ${baseVariables.mediaQuery.lg} {
            left: 17.38%;
            width: 65.23%;
            top: 22.22%;
          }
        `;
  }

  // Diagonal
  // Default → bottom-right;  Reverse → top-left (mirrors Default)
  // Offsets by breakpoint + aspectRatio:
  //   Mobile  2:3 → bot=11.90%/top=11.67%  |  1:1 → 14.81%
  //   Tablet  2:3 →  9.96%                 |  1:1 → 19.92%
  //   Desktop 2:3 →  8.40%                 |  1:1 → 19.26%
  // Width: Mobile=47.5%, Tablet=56.64%, Desktop=48.03%
  const mobileOffsetDefault = is1x1 ? '14.81%' : '11.90%';
  const mobileOffsetReverse = is1x1 ? '14.81%' : '11.67%';
  const tabletOffset = is1x1 ? '19.92%' : '9.96%';
  const desktopOffset = is1x1 ? '19.26%' : '8.40%';

  return isReverse
    ? css`
        left: 0;
        width: 47.5%;
        aspect-ratio: ${AR};
        top: ${mobileOffsetReverse};
        @media ${baseVariables.mediaQuery.md} {
          width: 56.64%;
          top: ${tabletOffset};
        }
        @media ${baseVariables.mediaQuery.lg} {
          width: 48.03%;
          top: ${desktopOffset};
        }
      `
    : css`
        right: 0;
        width: 47.5%;
        aspect-ratio: ${AR};
        bottom: ${mobileOffsetDefault};
        @media ${baseVariables.mediaQuery.md} {
          width: 56.64%;
          bottom: ${tabletOffset};
        }
        @media ${baseVariables.mediaQuery.lg} {
          width: 48.03%;
          bottom: ${desktopOffset};
        }
      `;
};

// ─── Exported Styled Components ───────────────────────────────────────────────

// Wrapper sets the intrinsic height via aspect-ratio for standalone use.
// Mobile: varies by imageDirection + aspectRatio (Figma measurements).
// Tablet/Desktop: fixed ratio — inside OEditorialHighlight height is overridden anyway.
export const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: 100%;
  background-color: var(--Color-set-brand-bg-color, #fff);
  aspect-ratio: ${props => wrapperMobileRatio(props.$imageDirection, props.$aspectRatio)};

  @media ${baseVariables.mediaQuery.md} {
    aspect-ratio: 286 / 512;
  }

  @media ${baseVariables.mediaQuery.lg} {
    aspect-ratio: 558 / 810;
  }
`;

// Primary (a-image): absolutely positioned, height derived from aspect-ratio CSS property.
export const PrimaryMedia = styled.div<MediaProps>`
  position: absolute;
  overflow: hidden;
  ${props => primaryCss(props.$imageDirection, props.$imageLayout, props.$aspectRatio)}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// Secondary (m-media-border): absolutely positioned, height derived from aspect-ratio CSS property.
export const SecondaryMedia = styled.div<MediaProps>`
  position: absolute;
  overflow: hidden;
  ${props => secondaryCss(props.$imageDirection, props.$imageLayout, props.$aspectRatio)}

  [data-component-name='m-web-ui-kit-MMediaBorder'] {
    width: 100%;
    height: 100%;
    aspect-ratio: unset;
  }
`;
